import React, { FC } from 'react';
import sortBy from 'lodash/sortBy';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { Body, Row, MainSection } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { DropdownMenu } from 'src/presentation/components/agent_desktop_components';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import { RootState } from 'src/actions/configuration/rootReducer';
import { STATUS_PERSISTING, STATUS_SUCCEEDED } from 'src/presentation/hooks/useResource';
import answerLanguagesResource from 'src/actions/answers/answerLanguagesResource';
import AudienceSelector from 'src/presentation/components/pages/shared/AudienceSelector';
import audiencesResource from 'src/actions/audiences/audiencesResource';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { useForm, FormEditorProps } from 'src/presentation/components/forms/Form';
import helpCenterResource from 'src/actions/helpCenter/helpCenterResource';
import Input, { InputType } from 'src/presentation/components/forms/Input';
import { default as Languages } from '@ad-scripts/domain/models/languages/answer_languages';
import useAudienceOptions from 'src/presentation/components/pages/shared/hooks/useAudienceOptions';
import useDocumentResource, { useResourceValue } from 'src/presentation/hooks/useDocumentResource';
import useResource from 'src/presentation/hooks/useResource';

const HelpCenterEditor: FC<FormEditorProps> = ({ errors, item, root }) => {
  const dispatch = useDispatch();
  const orgId = useSelector((state: RootState) => state.auth.orgId);

  const audiences = useResource(audiencesResource).values;
  const formattedAudiences = useAudienceOptions(audiences);

  const { persistingState } = useResourceValue(helpCenterResource);

  const formContext = useForm({
    defaultItem: {
      orgId,
      sections: [],
      numColumns: 1,
    },
    errors,
    onSave: ({ workingState }) => {
      dispatch(helpCenterResource.effects.replaceSiteConfig(workingState));
    },
    item,
    resetErrors: helpCenterResource.actions.resetErrors,
    root,
  });
  const { loadingState: languagesLoad, value: availableLangs } = useDocumentResource(answerLanguagesResource);

  const { onClose, onSave, isNew } = formContext;

  const answerLanguages = sortBy(
    availableLangs.map(v => {
      return {
        value: v,
        label: Languages[v],
      };
    }),
    v => v.language
  );

  if (languagesLoad !== STATUS_SUCCEEDED) {
    return null;
  }

  return (
    <Form context={formContext}>
      <H1>{isNew ? 'Create' : 'Edit'} Help Center</H1>

      <Body data-aid="help-center-editor">
        <MainSection>
          <Row>
            <Input
              data-aid="help-center-name"
              attr="name"
              autoFocus
              label="Name"
              placeholder="United Kingdom Site"
              description="The name of the Help Center in the Settings page. This name will not be displayed on the Help Center itself."
            />
          </Row>
          <Row>
            <Input
              data-aid="help-center-title"
              attr="mainTitle"
              label="Page Title"
              placeholder="Help Center"
              description="The text shown in a browser's title bar or a page's tab on the main Help Center page."
            />
          </Row>
          <MainSection
            header="Answers"
            description={'Select your preferred language and audience (optional) for your Help Center.'}
          >
            <Row>
              <Dropdown
                data-aid="help-center-lng"
                attr="lng"
                options={answerLanguages}
                label="Language"
                placeholder="Choose Language"
                description="If you have Answers in multiple languages, this ensures the correct Answers appear on this Help Center."
              />
            </Row>

            <Row>
              <AudienceSelector
                attr="audienceId"
                audienceOptions={formattedAudiences}
                description="If you have Answers for multiple audiences, this ensures the correct Answers appear on this Help Center."
                section="Help Center"
              />
            </Row>

            <Row>
              <ColsDropdown
                data-aid="help-center-numColumns"
                attr="numColumns"
                options={[
                  { label: 1, value: 1 },
                  { label: 2, value: 2 },
                  { label: 3, value: 3 },
                ]}
                label="# of Columns"
                description="Sections in the Help Center will be split into this many columns."
              />
            </Row>
            <Row>
              <RowsInput
                data-aid="help-center-maxRows"
                attr="maxRows"
                type={InputType.number}
                label="# of Preview Answers"
                placeholder="4"
                description='Each section will show this number of Answers. Any additional Answer will be available through a "See more" link.'
              />
            </Row>
          </MainSection>

          <MainSection
            header="Search"
            description="Assign the text for the search placeholder, search results header, and other navigation elements."
          >
            <Row>
              <Input
                description="The placeholder text in the search bar."
                data-aid="help-center-searchPlaceholder"
                attr="searchPlaceholder"
                label="Search Placeholder"
                placeholder="Enter Keyword"
              />
            </Row>
            <Row>
              <Input
                description="The header displayed above the search results on the results page. Note you can use the wildcard “%q” to display the specific search term entered by the customer. For example, if you write here “View %q results” and a customer enters “shoes”, the customer sees “View shoes results”."
                data-aid="help-center-searchResultsHeadingTemplate"
                attr="searchResultHeadingTemplate"
                label="Search Results Header"
                placeholder="Search results for %q"
              />
            </Row>
            <Row>
              <Input
                description="The text displayed to return to the main Help Center Page when viewing an Answer."
                data-aid="help-center-backLink"
                attr="backLink"
                label="Back Link Text"
                placeholder="Back"
              />
            </Row>
            <Row>
              <Input
                description="The text of the link displayed at the bottom of the main Help Center page which links to an index page of all public Answers."
                data-aid="help-center-moreResultsLink"
                attr="moreResultsLink"
                label="More Results Link Text"
                placeholder="See more results"
              />
            </Row>
          </MainSection>
          <MainSection header="Sections" description="Assign the text for the section header and “See more” link.">
            <Row>
              <Input
                data-aid="help-center-faqHeading"
                attr="faqHeading"
                label="Sections Heading"
                description="The header displayed below the search bar and above the sections."
                placeholder="FAQs"
              />
            </Row>
            <Row>
              <Input
                data-aid="help-center-seeMoreLinkText"
                attr="seeMoreLinkText"
                label="See More Link Text"
                description="The text of the link to view more Answers."
                placeholder="See more"
              />
            </Row>
          </MainSection>
        </MainSection>
      </Body>

      <Footer>
        <Button data-aid="help-center-cancel" type={Button.Types.TEXT} onClick={onClose}>
          Cancel
        </Button>
        <Button data-aid="help-center-save" onClick={onSave} isLoading={persistingState === STATUS_PERSISTING}>
          {isNew ? 'Create Help Center' : 'Save'}
        </Button>
      </Footer>
    </Form>
  );
};

const RowsInput = styled(Input)`
  max-width: 80px;
`;

const ColsDropdown = styled(Dropdown)`
  ${DropdownMenu} {
    max-width: 80px;
  }
`;

export default HelpCenterEditor;
